import { useState, useEffect } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { readingService } from '../../lib/reading-service';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import AvatarOrInitial from '../Family/AvatarOrInitial';
import { 
  Trophy, 
  Target, 
  Calendar, 
  DollarSign, 
  Users, 
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  Award,
  BookOpen,
  TrendingUp,
  Clock,
  ChevronDown,
  ChevronUp, Star
} from 'lucide-react';

export default function AdminReadingChallenges() {
  const { currentUser, family } = useFamilyContext();
  const [challenges, setChallenges] = useState([]);
  const [kidProgress, setKidProgress] = useState({});
  const [expandedKid, setExpandedKid] = useState(null);
  const [editingChallenge, setEditingChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Form state for creating/editing challenges
  const [challengeForm, setChalllengeForm] = useState({
    kidId: '',
    challengeName: '',
    challengeDescription: '',
    challengeType: 'points', // 'books' or 'points' or 'pages'
    targetBooks: 5,
    targetPoints: 100,
    targetPages: 1000,
    pointsPerBook: 10,
    pointsPerPage: 0.1,
    bonusPointsLongBook: 5, // bonus for books over 300 pages
    bonusPointsGenreDiversity: 10, // bonus for reading different genres
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    rewardAmount: 10,
    rewardDescription: 'Complete the reading challenge',
    milestoneRewards: [
      { threshold: 25, amount: 2, description: '25% progress bonus' },
      { threshold: 50, amount: 3, description: '50% progress bonus' },
      { threshold: 75, amount: 4, description: '75% progress bonus' }
    ]
  });

  useEffect(() => {
    if (currentUser?.isParent) {
      loadChallengesAndProgress();
    }
  }, [currentUser]);

  // Auto-switch to create tab if no challenges exist
  useEffect(() => {
    if (!loading && challenges.length === 0 && activeTab === 'overview') {
      setActiveTab('create');
    }
  }, [loading, challenges.length, activeTab]);

  const loadChallengesAndProgress = async () => {
    try {
      setLoading(true);
      
      // Get all kids in the family
      const kidIds = family?.kids?.map(kid => kid.id) || [];
      
      // Load active challenges for all kids
      const challengePromises = kidIds.map(async (kidId) => {
        const challenge = await readingService.getActiveChallenge(kidId);
        return { challenge, kidId };
      });
      const challengeResults = await Promise.all(challengePromises);
      
      // Filter out null results and map with correct kid info
      const activeChallenges = challengeResults
        .filter(result => result.challenge !== null)
        .map(result => {
          const kid = family.kids.find(k => k.id === result.kidId);
          return {
            ...result.challenge,
            kidName: kid?.name || 'Unknown',
            kidId: result.kidId
          };
        });
      
      setChallenges(activeChallenges);
      
      // Load progress for each kid
      const progressPromises = kidIds.map(async (kidId) => {
        const stats = await readingService.getReadingStats(kidId);
        const streak = await readingService.getCurrentStreak(kidId);
        const currentBook = await readingService.getCurrentBook(kidId);
        const recentBooks = await readingService.getReadingList(kidId, 'completed');
        
        return {
          kidId,
          stats,
          streak,
          currentBook,
          recentBooks: recentBooks.slice(0, 5)
        };
      });
      
      const progressResults = await Promise.all(progressPromises);
      const progressMap = {};
      progressResults.forEach(p => {
        progressMap[p.kidId] = p;
      });
      
      setKidProgress(progressMap);
    } catch (error) {
      console.error('Error loading challenges and progress:', error);
      toast.error('Failed to load reading data');
    } finally {
      setLoading(false);
    }
  };

  const calculateCurrentPoints = (kidId) => {
    const progress = kidProgress[kidId];
    if (!progress) return 0;
    
    // Calculate points based on current challenge settings
    let points = 0;
    
    // Points from completed books
    if (progress.stats?.booksRead) {
      points += progress.stats.booksRead * (challengeForm.pointsPerBook || 10);
    }
    
    // Points from pages read (approximate)
    if (progress.stats?.totalPages) {
      points += progress.stats.totalPages * (challengeForm.pointsPerPage || 0.1);
    }
    
    return Math.round(points);
  };

  const handleCreateChallenge = async () => {
    try {
      const challengeData = {
        name: challengeForm.challengeName,
        description: challengeForm.challengeDescription,
        challengeType: challengeForm.challengeType,
        targetBooks: parseInt(challengeForm.targetBooks),
        targetPoints: parseInt(challengeForm.targetPoints),
        targetPages: parseInt(challengeForm.targetPages),
        startDate: challengeForm.startDate,
        endDate: challengeForm.endDate,
        rewardAmount: parseFloat(challengeForm.rewardAmount),
        rewardDescription: challengeForm.rewardDescription,
        pointsConfig: {
          pointsPerBook: parseFloat(challengeForm.pointsPerBook),
          pointsPerPage: parseFloat(challengeForm.pointsPerPage),
          bonusPointsLongBook: parseFloat(challengeForm.bonusPointsLongBook),
          bonusPointsGenreDiversity: parseFloat(challengeForm.bonusPointsGenreDiversity)
        },
        milestoneRewards: challengeForm.milestoneRewards
      };
      
      await readingService.createReadingChallenge(challengeForm.kidId, challengeData);
      
      toast.success('Reading challenge created!');
      await loadChallengesAndProgress();
      setActiveTab('overview'); // Switch to overview after creating
      
      // Reset form
      setChalllengeForm({
        kidId: '',
        challengeName: '',
        challengeDescription: '',
        challengeType: 'points',
        targetBooks: 5,
        targetPoints: 100,
        targetPages: 1000,
        pointsPerBook: 10,
        pointsPerPage: 0.1,
        bonusPointsLongBook: 5,
        bonusPointsGenreDiversity: 10,
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        rewardAmount: 10,
        rewardDescription: 'Complete the reading challenge',
        milestoneRewards: [
          { threshold: 25, amount: 2, description: '25% progress bonus' },
          { threshold: 50, amount: 3, description: '50% progress bonus' },
          { threshold: 75, amount: 4, description: '75% progress bonus' }
        ]
      });
    } catch (error) {
      console.error('Error creating challenge:', error);
      toast.error('Failed to create challenge');
    }
  };

  const handleUpdateChallenge = async (challengeId, updates) => {
    try {
      const { error } = await supabase
        .from('reading_challenges')
        .update(updates)
        .eq('id', challengeId);
        
      if (error) throw error;
      
      toast.success('Challenge updated!');
      loadChallengesAndProgress();
      setEditingChallenge(null);
    } catch (error) {
      console.error('Error updating challenge:', error);
      toast.error('Failed to update challenge');
    }
  };

  const handleDeleteChallenge = async (challengeId) => {
    if (!confirm('Are you sure you want to delete this challenge?')) return;
    
    try {
      const { error } = await supabase
        .from('reading_challenges')
        .update({ status: 'cancelled' })
        .eq('id', challengeId);
        
      if (error) throw error;
      
      toast.success('Challenge cancelled');
      loadChallengesAndProgress();
    } catch (error) {
      console.error('Error deleting challenge:', error);
      toast.error('Failed to delete challenge');
    }
  };

  const getKidById = (kidId) => {
    return family?.kids?.find(kid => kid.id === kidId);
  };

  if (!currentUser?.isParent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <h2 className="text-2xl font-bold text-slate-800 mb-4">Parent Access Required</h2>
            <p className="text-slate-600">This page is only accessible to parents.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 p-4 sm:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3">
                <Trophy className="w-8 h-8 text-amber-500" />
                Reading Challenge Admin
              </h1>
              <p className="text-slate-600 mt-2">Manage reading challenges and track progress for all kids</p>
            </div>
          </div>
        </div>

        {/* Challenge Tabs */}
        <div className="bg-white rounded-2xl shadow-lg mb-8">
          <div className="border-b border-stone-200">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('overview')}
                className={`py-4 px-2 border-b-2 font-medium text-sm ${
                  activeTab === 'overview'
                    ? 'border-amber-500 text-amber-600'
                    : 'border-transparent text-stone-500 hover:text-slate-700 hover:border-stone-300'
                }`}
              >
                Overview
              </button>
              
              {challenges.map(challenge => {
                const kid = family?.kids?.find(k => k.id === challenge.kidId);
                return (
                  <button
                    key={challenge.id}
                    onClick={() => setActiveTab(challenge.id)}
                    className={`py-4 px-2 border-b-2 font-medium text-sm flex items-center gap-2 ${
                      activeTab === challenge.id
                        ? 'border-amber-500 text-amber-600'
                        : 'border-transparent text-stone-500 hover:text-slate-700 hover:border-stone-300'
                    }`}
                  >
                    <AvatarOrInitial avatar={kid?.avatar} name={kid?.name} size="xs" className="w-6 h-6" />
                    {kid?.name || 'Unknown'}
                    <span className="text-xs bg-stone-100 text-slate-600 px-2 py-1 rounded-full">
                      {challenge.challenge_type === 'books' 
                        ? `${challenge.current_books || 0}/${challenge.target_books}`
                        : challenge.challenge_type === 'points'
                        ? `${calculateCurrentPoints(challenge.kidId)}/${challenge.target_points}`
                        : `${kidProgress[challenge.kidId]?.stats?.totalPages || 0}/${challenge.target_pages}`
                      }
                    </span>
                  </button>
                );
              })}
              
              <button
                onClick={() => setActiveTab('create')}
                className={`py-4 px-2 border-b-2 font-medium text-sm flex items-center gap-2 ${
                  activeTab === 'create'
                    ? 'border-amber-500 text-amber-600'
                    : 'border-transparent text-stone-500 hover:text-slate-700 hover:border-stone-300'
                }`}
              >
                <Plus className="w-4 h-4" />
                Create New
              </button>
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <>
            {/* Active Challenges Overview */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
              <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                <Target className="w-5 h-5" />
                Active Challenges
              </h2>
              
              {loading ? (
                <div className="text-center py-8 text-stone-500">Loading challenges...</div>
              ) : challenges.length === 0 ? (
                <div className="text-center py-8 text-stone-500">
                  No active challenges. Create one using the "Create New" tab!
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {challenges.map(challenge => {
                    const kid = family?.kids?.find(k => k.id === challenge.kidId);
                    const currentProgress = challenge.challenge_type === 'books' 
                      ? ((challenge.current_books || 0) / challenge.target_books) * 100
                      : challenge.challenge_type === 'points'
                      ? (calculateCurrentPoints(challenge.kidId) / challenge.target_points) * 100
                      : challenge.challenge_type === 'pages'
                      ? ((kidProgress[challenge.kidId]?.stats?.totalPages || 0) / challenge.target_pages) * 100
                      : 0;
                    
                    return (
                      <div key={challenge.id} className="border border-stone-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-3 mb-3">
                          <AvatarOrInitial avatar={kid?.avatar} name={kid?.name} size="md" className="w-8 h-8" />
                          <div>
                            <h3 className="font-semibold text-lg">{kid?.name || 'Unknown'}</h3>
                            <p className="text-sm text-slate-600">{challenge.challenge_name}</p>
                          </div>
                        </div>
                        
                        <div className="mb-3">
                          <div className="flex justify-between text-sm text-slate-600 mb-1">
                            <span>Progress</span>
                            <span>{Math.round(currentProgress)}%</span>
                          </div>
                          <div className="w-full bg-stone-200 rounded-full h-2">
                            <div 
                              className="bg-gradient-to-r from-amber-400 to-amber-500 h-2 rounded-full transition-all"
                              style={{ width: `${Math.min(currentProgress, 100)}%` }}
                            />
                          </div>
                        </div>
                        
                        <div className="text-sm text-slate-600 space-y-1">
                          {challenge.challenge_type === 'books' && (
                            <p>{challenge.current_books || 0} of {challenge.target_books} books</p>
                          )}
                          {challenge.challenge_type === 'points' && (
                            <p>{calculateCurrentPoints(challenge.kidId)} of {challenge.target_points} points</p>
                          )}
                          {challenge.challenge_type === 'pages' && (
                            <p>{kidProgress[challenge.kidId]?.stats?.totalPages || 0} of {challenge.target_pages} pages</p>
                          )}
                          <p>Ends {new Date(challenge.end_date).toLocaleDateString()}</p>
                          <p className="text-emerald-600 font-medium">${challenge.reward_amount} reward</p>
                        </div>
                        
                        <button
                          onClick={() => setActiveTab(challenge.id)}
                          className="w-full mt-3 px-3 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition-colors text-sm font-medium"
                        >
                          View Details
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            {/* Kid Progress Overview */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Progress Overview
              </h2>
              
              <div className="space-y-4">
                {family?.kids?.map(kid => {
                  const progress = kidProgress[kid.id];
                  const isExpanded = expandedKid === kid.id;
                  
                  return (
                    <div key={kid.id} className="border border-stone-200 rounded-lg">
                      <div 
                        className="p-4 cursor-pointer hover:bg-stone-50 transition-colors"
                        onClick={() => setExpandedKid(isExpanded ? null : kid.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <AvatarOrInitial 
                              user={kid} 
                              size="md" 
                              className="w-10 h-10"
                            />
                            <div>
                              <h3 className="font-semibold text-lg">{kid.name}</h3>
                              {progress && (
                                <div className="flex gap-4 text-sm text-slate-600">
                                  <span>{progress.stats?.booksRead || 0} books read</span>
                                  <span>{progress.streak?.current_streak || 0} day streak</span>
                                  <span>${progress.stats?.totalRewards?.toFixed(2) || '0.00'} earned</span>
                                </div>
                              )}
                            </div>
                          </div>
                          {isExpanded ? <ChevronUp /> : <ChevronDown />}
                        </div>
                      </div>
                      
                      {isExpanded && progress && (
                        <div className="px-4 pb-4 border-t border-stone-200">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            {/* Current Book */}
                            {progress.currentBook && (
                              <div className="bg-amber-50 rounded-lg p-4">
                                <h4 className="font-semibold text-sm text-amber-800 mb-2">Currently Reading</h4>
                                <p className="font-medium">{progress.currentBook.book_title}</p>
                                <p className="text-sm text-slate-600">by {progress.currentBook.book_author}</p>
                                <div className="mt-2">
                                  <div className="flex justify-between text-sm text-slate-600 mb-1">
                                    <span>Progress</span>
                                    <span>{progress.currentBook.current_page}/{progress.currentBook.total_pages} pages</span>
                                  </div>
                                  <div className="w-full bg-stone-200 rounded-full h-2">
                                    <div 
                                      className="bg-amber-400 h-2 rounded-full"
                                      style={{ 
                                        width: `${(progress.currentBook.current_page / progress.currentBook.total_pages) * 100}%` 
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                            
                            {/* Recent Books */}
                            <div className="bg-azure-50 rounded-lg p-4">
                              <h4 className="font-semibold text-sm text-azure-800 mb-2">Recent Books</h4>
                              {progress.recentBooks?.length > 0 ? (
                                <ul className="space-y-1">
                                  {progress.recentBooks.map((book, index) => (
                                    <li key={index} className="text-sm">
                                      <span className="font-medium">{book.book_title}</span>
                                      <span className="text-slate-600"> - {new Date(book.completed_at).toLocaleDateString()}</span>
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="text-sm text-slate-600">No completed books yet</p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
        
        {/* Individual Challenge Tabs */}
        {challenges.map(challenge => {
          if (activeTab !== challenge.id) return null;
          
          const kid = family?.kids?.find(k => k.id === challenge.kidId);
          const progress = kidProgress[challenge.kidId];
          const currentProgress = challenge.challenge_type === 'books' 
            ? ((challenge.current_books || 0) / challenge.target_books) * 100
            : challenge.challenge_type === 'points'
            ? (calculateCurrentPoints(challenge.kidId) / challenge.target_points) * 100
            : challenge.challenge_type === 'pages'
            ? ((progress?.stats?.totalPages || 0) / challenge.target_pages) * 100
            : 0;
          
          return (
            <div key={challenge.id} className="space-y-6">
              {/* Challenge Details */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <AvatarOrInitial avatar={kid?.avatar} name={kid?.name} size="lg" className="w-12 h-12" />
                    <div>
                      <h2 className="text-2xl font-bold text-slate-800">{challenge.challenge_name}</h2>
                      <p className="text-slate-600">{kid?.name}'s Reading Challenge</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingChallenge(challenge)}
                      className="p-2 text-azure-600 hover:bg-azure-50 rounded-lg transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteChallenge(challenge.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {/* Progress Display */}
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 mb-6">
                  <div className="text-center mb-4">
                    <div className="text-3xl font-bold text-amber-600">
                      {Math.round(currentProgress)}%
                    </div>
                    <div className="text-slate-600">Complete</div>
                  </div>
                  
                  <div className="w-full bg-stone-200 rounded-full h-4 mb-4">
                    <div 
                      className="bg-gradient-to-r from-amber-400 to-amber-500 h-4 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(currentProgress, 100)}%` }}
                    />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 text-center text-sm">
                    <div>
                      <div className="font-semibold text-amber-600">
                        {challenge.challenge_type === 'books' 
                          ? (challenge.current_books || 0)
                          : challenge.challenge_type === 'points'
                          ? calculateCurrentPoints(challenge.kidId)
                          : (progress?.stats?.totalPages || 0)
                        }
                      </div>
                      <div className="text-slate-600">
                        {challenge.challenge_type === 'books' ? 'Books Read' 
                          : challenge.challenge_type === 'points' ? 'Points Earned'
                          : 'Pages Read'
                        }
                      </div>
                    </div>
                    <div>
                      <div className="font-semibold text-amber-600">
                        {challenge.challenge_type === 'books' 
                          ? (challenge.target_books - (challenge.current_books || 0))
                          : challenge.challenge_type === 'points'
                          ? (challenge.target_points - calculateCurrentPoints(challenge.kidId))
                          : (challenge.target_pages - (progress?.stats?.totalPages || 0))
                        }
                      </div>
                      <div className="text-slate-600">Remaining</div>
                    </div>
                    <div>
                      <div className="font-semibold text-emerald-600">
                        ${challenge.reward_amount}
                      </div>
                      <div className="text-slate-600">Reward</div>
                    </div>
                  </div>
                </div>
                
                {/* Milestone Rewards */}
                {challenge.milestone_rewards && challenge.milestone_rewards.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-3">Milestone Rewards</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {challenge.milestone_rewards.map((milestone, index) => {
                        const isAchieved = currentProgress >= milestone.threshold;
                        
                        return (
                          <div 
                            key={index}
                            className={`p-3 rounded-lg border-2 ${isAchieved 
                              ? 'bg-emerald-50 border-emerald-200 text-emerald-700' 
                              : 'bg-stone-50 border-stone-200 text-slate-600'
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              {isAchieved && <Award className="w-4 h-4" />}
                              <span className="font-semibold">{milestone.threshold}% Progress</span>
                            </div>
                            <p className="text-sm">{milestone.description}</p>
                            <p className="text-lg font-bold">${milestone.amount}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Challenge Info */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div className="bg-stone-50 rounded-lg p-3">
                    <div className="text-slate-600">Type</div>
                    <div className="font-semibold capitalize">{challenge.challenge_type}</div>
                  </div>
                  <div className="bg-stone-50 rounded-lg p-3">
                    <div className="text-slate-600">Started</div>
                    <div className="font-semibold">{new Date(challenge.start_date).toLocaleDateString()}</div>
                  </div>
                  <div className="bg-stone-50 rounded-lg p-3">
                    <div className="text-slate-600">Ends</div>
                    <div className="font-semibold">{new Date(challenge.end_date).toLocaleDateString()}</div>
                  </div>
                  <div className="bg-stone-50 rounded-lg p-3">
                    <div className="text-slate-600">Status</div>
                    <div className="font-semibold text-emerald-600">Active</div>
                  </div>
                </div>
              </div>
              
              {/* Current Reading Progress */}
              {progress && (
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="text-lg font-semibold mb-4">{kid?.name}'s Reading Activity</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Current Book */}
                    {progress.currentBook && (
                      <div className="bg-amber-50 rounded-lg p-4">
                        <h4 className="font-semibold text-amber-800 mb-3">Currently Reading</h4>
                        <p className="font-medium text-lg">{progress.currentBook.book_title}</p>
                        <p className="text-slate-600 mb-3">by {progress.currentBook.book_author}</p>
                        <div>
                          <div className="flex justify-between text-sm text-slate-600 mb-2">
                            <span>Progress</span>
                            <span>{progress.currentBook.current_page}/{progress.currentBook.total_pages} pages</span>
                          </div>
                          <div className="w-full bg-stone-200 rounded-full h-3">
                            <div 
                              className="bg-amber-400 h-3 rounded-full"
                              style={{ 
                                width: `${(progress.currentBook.current_page / progress.currentBook.total_pages) * 100}%` 
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Reading Stats */}
                    <div className="bg-azure-50 rounded-lg p-4">
                      <h4 className="font-semibold text-azure-800 mb-3">Reading Stats</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span>Books Completed:</span>
                          <span className="font-semibold">{progress.stats?.booksRead || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Current Streak:</span>
                          <span className="font-semibold">{progress.streak?.current_streak || 0} days</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Total Rewards:</span>
                          <span className="font-semibold text-emerald-600">${progress.stats?.totalRewards?.toFixed(2) || '0.00'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Recent Books */}
                  {progress.recentBooks?.length > 0 && (
                    <div className="mt-6">
                      <h4 className="font-semibold mb-3">Recently Completed Books</h4>
                      <div className="space-y-2">
                        {progress.recentBooks.map((book, index) => (
                          <div key={index} className="flex justify-between items-center p-2 bg-stone-50 rounded">
                            <span className="font-medium">{book.book_title}</span>
                            <span className="text-sm text-slate-600">{new Date(book.completed_at).toLocaleDateString()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        
        {/* Create New Challenge Tab */}
        {activeTab === 'create' && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
            <Plus className="w-5 h-5" />
            Create New Challenge
          </h2>
          
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Select Kid
                </label>
                <select
                  value={challengeForm.kidId}
                  onChange={(e) => setChalllengeForm({...challengeForm, kidId: e.target.value})}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                >
                  <option value="">Choose a kid...</option>
                  {family?.kids?.map(kid => (
                    <option key={kid.id} value={kid.id}>
                      <AvatarOrInitial avatar={kid.avatar} name={kid.name} size="xs" className="w-5 h-5" /> {kid.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Challenge Name
                </label>
                <input
                  type="text"
                  value={challengeForm.challengeName}
                  onChange={(e) => setChalllengeForm({...challengeForm, challengeName: e.target.value})}
                  placeholder="Summer Reading Challenge"
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Challenge Type */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Challenge Type
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => setChalllengeForm({...challengeForm, challengeType: 'books'})}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    challengeForm.challengeType === 'books'
                      ? 'border-azure-500 bg-azure-50 text-azure-700'
                      : 'border-stone-200 hover:border-stone-300'
                  }`}
                >
                  <BookOpen className="w-6 h-6 mx-auto mb-2" />
                  <div className="font-medium">Books Goal</div>
                  <div className="text-sm text-slate-600">Simple book count target</div>
                </button>
                
                <button
                  type="button"
                  onClick={() => setChalllengeForm({...challengeForm, challengeType: 'points'})}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    challengeForm.challengeType === 'points'
                      ? 'border-amber-500 bg-amber-50 text-amber-700'
                      : 'border-stone-200 hover:border-stone-300'
                  }`}
                >
                  <Star className="w-6 h-6 mx-auto mb-2" />
                  <div className="font-medium">Points System</div>
                  <div className="text-sm text-slate-600">Earn points for reading</div>
                </button>
                
                <button
                  type="button"
                  onClick={() => setChalllengeForm({...challengeForm, challengeType: 'pages'})}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    challengeForm.challengeType === 'pages'
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                      : 'border-stone-200 hover:border-stone-300'
                  }`}
                >
                  <Clock className="w-6 h-6 mx-auto mb-2" />
                  <div className="font-medium">Pages Goal</div>
                  <div className="text-sm text-slate-600">Target total pages read</div>
                </button>
              </div>
            </div>

            {/* Challenge Targets */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {challengeForm.challengeType === 'books' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Target Books
                  </label>
                  <input
                    type="number"
                    value={challengeForm.targetBooks}
                    onChange={(e) => setChalllengeForm({...challengeForm, targetBooks: e.target.value})}
                    min="1"
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                </div>
              )}
              
              {challengeForm.challengeType === 'points' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Target Points
                  </label>
                  <input
                    type="number"
                    value={challengeForm.targetPoints}
                    onChange={(e) => setChalllengeForm({...challengeForm, targetPoints: e.target.value})}
                    min="1"
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                </div>
              )}
              
              {challengeForm.challengeType === 'pages' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Target Pages
                  </label>
                  <input
                    type="number"
                    value={challengeForm.targetPages}
                    onChange={(e) => setChalllengeForm({...challengeForm, targetPages: e.target.value})}
                    min="1"
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Completion Reward ($)
                </label>
                <input
                  type="number"
                  value={challengeForm.rewardAmount}
                  onChange={(e) => setChalllengeForm({...challengeForm, rewardAmount: e.target.value})}
                  min="0"
                  step="0.50"
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Points Configuration (only show for points type) */}
            {challengeForm.challengeType === 'points' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h3 className="font-medium text-amber-800 mb-3">Points Configuration</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-amber-700 mb-1">
                      Points per Book
                    </label>
                    <input
                      type="number"
                      value={challengeForm.pointsPerBook}
                      onChange={(e) => setChalllengeForm({...challengeForm, pointsPerBook: e.target.value})}
                      step="1"
                      className="w-full px-2 py-1 text-sm border border-amber-300 rounded focus:ring-1 focus:ring-amber-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-amber-700 mb-1">
                      Points per Page
                    </label>
                    <input
                      type="number"
                      value={challengeForm.pointsPerPage}
                      onChange={(e) => setChalllengeForm({...challengeForm, pointsPerPage: e.target.value})}
                      step="0.1"
                      className="w-full px-2 py-1 text-sm border border-amber-300 rounded focus:ring-1 focus:ring-amber-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-amber-700 mb-1">
                      Long Book Bonus
                    </label>
                    <input
                      type="number"
                      value={challengeForm.bonusPointsLongBook}
                      onChange={(e) => setChalllengeForm({...challengeForm, bonusPointsLongBook: e.target.value})}
                      step="1"
                      className="w-full px-2 py-1 text-sm border border-amber-300 rounded focus:ring-1 focus:ring-amber-500"
                    />
                    <div className="text-xs text-amber-600 mt-1">300+ pages</div>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-amber-700 mb-1">
                      Genre Diversity Bonus
                    </label>
                    <input
                      type="number"
                      value={challengeForm.bonusPointsGenreDiversity}
                      onChange={(e) => setChalllengeForm({...challengeForm, bonusPointsGenreDiversity: e.target.value})}
                      step="1"
                      className="w-full px-2 py-1 text-sm border border-amber-300 rounded focus:ring-1 focus:ring-amber-500"
                    />
                    <div className="text-xs text-amber-600 mt-1">Per new genre</div>
                  </div>
                </div>
              </div>
            )}

            {/* Milestone Rewards Configuration */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <h3 className="font-medium text-emerald-800 mb-3 flex items-center gap-2">
                <Award className="w-5 h-5" />
                Milestone Rewards
              </h3>
              <p className="text-sm text-emerald-700 mb-3">
                Reward progress milestones to keep kids motivated throughout the challenge.
              </p>
              
              <div className="space-y-3">
                {challengeForm.milestoneRewards.map((milestone, index) => (
                  <div key={index} className="flex items-center gap-3 bg-white p-3 rounded-lg border border-emerald-200">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-emerald-700">{milestone.threshold}%</span>
                      <span className="text-sm text-slate-600">progress:</span>
                    </div>
                    
                    <div className="flex items-center gap-2 flex-1">
                      <span className="text-sm">$</span>
                      <input
                        type="number"
                        value={milestone.amount}
                        onChange={(e) => {
                          const newMilestones = [...challengeForm.milestoneRewards];
                          newMilestones[index].amount = parseFloat(e.target.value) || 0;
                          setChalllengeForm({...challengeForm, milestoneRewards: newMilestones});
                        }}
                        step="0.50"
                        min="0"
                        className="w-16 px-2 py-1 text-sm border border-emerald-300 rounded focus:ring-1 focus:ring-emerald-500"
                      />
                      
                      <input
                        type="text"
                        value={milestone.description}
                        onChange={(e) => {
                          const newMilestones = [...challengeForm.milestoneRewards];
                          newMilestones[index].description = e.target.value;
                          setChalllengeForm({...challengeForm, milestoneRewards: newMilestones});
                        }}
                        placeholder="Milestone description"
                        className="flex-1 px-2 py-1 text-sm border border-emerald-300 rounded focus:ring-1 focus:ring-emerald-500"
                      />
                    </div>
                    
                    <button
                      type="button"
                      onClick={() => {
                        const newMilestones = challengeForm.milestoneRewards.filter((_, i) => i !== index);
                        setChalllengeForm({...challengeForm, milestoneRewards: newMilestones});
                      }}
                      className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                
                <button
                  type="button"
                  onClick={() => {
                    const newMilestones = [...challengeForm.milestoneRewards, {
                      threshold: 90,
                      amount: 5,
                      description: 'Almost there bonus'
                    }];
                    setChalllengeForm({...challengeForm, milestoneRewards: newMilestones});
                  }}
                  className="w-full p-2 border-2 border-dashed border-emerald-300 text-emerald-600 rounded-lg hover:bg-emerald-50 transition-colors text-sm"
                >
                  + Add Milestone Reward
                </button>
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Start Date
                </label>
              <input
                type="date"
                value={challengeForm.startDate}
                onChange={(e) => setChalllengeForm({...challengeForm, startDate: e.target.value})}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={challengeForm.endDate}
                onChange={(e) => setChalllengeForm({...challengeForm, endDate: e.target.value})}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Description
              </label>
              <textarea
                value={challengeForm.challengeDescription}
                onChange={(e) => setChalllengeForm({...challengeForm, challengeDescription: e.target.value})}
                placeholder="Read amazing books and earn rewards!"
                rows="2"
                className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <button
            onClick={handleCreateChallenge}
            disabled={!challengeForm.kidId || !challengeForm.challengeName}
            className="mt-4 px-6 py-2 bg-amber-500 text-white font-medium rounded-lg hover:bg-amber-600 transition-colors disabled:bg-stone-300"
          >
            Create Challenge
          </button>
        </div>
        )}

        
      </div>

      {/* Edit Challenge Modal */}
      {editingChallenge && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Edit Challenge</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Target Books
                </label>
                <input
                  type="number"
                  value={editingChallenge.target_books}
                  onChange={(e) => setEditingChallenge({
                    ...editingChallenge, 
                    target_books: parseInt(e.target.value)
                  })}
                  min="1"
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Reward Amount ($)
                </label>
                <input
                  type="number"
                  value={editingChallenge.reward_amount}
                  onChange={(e) => setEditingChallenge({
                    ...editingChallenge, 
                    reward_amount: parseFloat(e.target.value)
                  })}
                  min="0"
                  step="0.50"
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={editingChallenge.end_date.split('T')[0]}
                  onChange={(e) => setEditingChallenge({
                    ...editingChallenge, 
                    end_date: e.target.value
                  })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                />
              </div>
            </div>
            
            <div className="flex gap-2 mt-6">
              <button
                onClick={() => handleUpdateChallenge(editingChallenge.id, {
                  target_books: editingChallenge.target_books,
                  reward_amount: editingChallenge.reward_amount,
                  end_date: editingChallenge.end_date
                })}
                className="flex-1 px-4 py-2 bg-amber-500 text-white font-medium rounded-lg hover:bg-amber-600"
              >
                Save Changes
              </button>
              <button
                onClick={() => setEditingChallenge(null)}
                className="flex-1 px-4 py-2 bg-stone-200 text-slate-700 font-medium rounded-lg hover:bg-stone-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  
  );
}